<template>
  <div class="permission-app">
  <div class="permission-resources-view bg-slate-50">
    <permission-page-header
      icon="mdi-swap-vertical"
      variant="violet"
      :title="$t('permission_resources_page_title')"
      :subtitle="$t('permission_resources_page_desc')"
      :stats="headerStats"
    />
    <permission-sub-tabs />

    <div class="p-4 max-w-7xl mx-auto">
      <!-- Info banner（padding 適中、字體 sm；視覺 < 主內容但可讀）-->
      <div class="flex items-start gap-2.5 px-4 py-2.5 bg-amber-50/70 border border-amber-200/70 rounded-xl mb-4 text-sm leading-snug text-amber-800">
        <v-icon size="16" color="#d97706" class="shrink-0 mt-0.5" aria-hidden="true">mdi-alert-outline</v-icon>
        <div>{{ $t('permission_resources_banner') }}</div>
      </div>

      <!-- Governance dashboard：兩條進度條 + reverse-orphan 摘要 -->
      <div v-if="!loading && items.length" class="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <!-- 進度條 1：enforce 覆蓋率 -->
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 space-y-1.5">
          <div class="flex items-center justify-between text-sm">
            <span class="font-semibold text-slate-700">{{ $t('permission_enforce_coverage') }}</span>
            <span class="tabular-nums font-bold text-emerald-700">{{ enforceCoverage }}%</span>
          </div>
          <div class="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              class="h-full rounded-full bg-emerald-500 transition-all duration-500"
              :style="{ width: enforceCoverage + '%' }"
            />
          </div>
          <div class="text-xs text-slate-400">{{ $t('permission_enforce_coverage_desc') }}</div>
        </div>
        <!-- 進度條 2：健康率 -->
        <div class="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3 space-y-1.5">
          <div class="flex items-center justify-between text-sm">
            <span class="font-semibold text-slate-700">{{ $t('permission_health_rate') }}</span>
            <span class="tabular-nums font-bold text-sky-700">{{ healthRate }}%</span>
          </div>
          <div class="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              class="h-full rounded-full bg-sky-500 transition-all duration-500"
              :style="{ width: healthRate + '%' }"
            />
          </div>
          <div class="text-xs text-slate-400">{{ $t('permission_health_rate_desc') }}</div>
        </div>
      </div>

      <!-- Reverse-orphan 摘要：前端宣告但 DB 未 seed 的 key -->
      <div v-if="!loading && reverseOrphanKeys.length" class="mb-4 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
        <div class="flex items-center gap-2 mb-2 text-sm font-semibold text-violet-700">
          <v-icon size="16" color="#7c3aed" aria-hidden="true">mdi-code-braces</v-icon>
          {{ $t('permission_reverse_orphan_title') }}
          <span class="ml-auto text-xs font-normal text-violet-500">{{ reverseOrphanKeys.length }}</span>
        </div>
        <div class="flex flex-wrap gap-1.5">
          <code
            v-for="key in reverseOrphanKeys"
            :key="key"
            class="text-xs px-2 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200 font-mono"
            :title="$t('permission_reverse_orphan_hint')"
          >{{ key }}</code>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="space-y-3">
        <div v-for="i in 4" :key="i" class="animate-pulse flex items-center gap-3 bg-white rounded-2xl p-4">
          <div class="h-5 bg-slate-200 rounded flex-1"></div>
          <div class="h-5 bg-slate-200 rounded w-24"></div>
          <div class="h-5 bg-slate-200 rounded w-10"></div>
        </div>
      </div>

      <!-- Category sections -->
      <div v-else-if="filteredGroups.length > 0">
        <div
          v-for="group in filteredGroups"
          :key="group.category"
          class="mb-4"
        >
          <!-- Section header（violet 左直條呼應頁面 variant；leading-none 確保文字置中）-->
          <div class="flex items-center gap-3 mb-2 px-1 leading-none">
            <span class="border-l-[3px] border-violet-500 pl-2.5 text-sm font-bold text-slate-700">
              {{ group.category || $t('permission_uncategorized') }}
            </span>
            <div class="flex-1 h-px bg-slate-200" aria-hidden="true"></div>
            <span class="text-xs text-slate-400 tabular-nums">
              {{ $t('permission_category_count', { count: group.rows.length }) }}
            </span>
          </div>

          <!-- Resource cards（容器樣式對齊 UserPermissionMatrix：rounded-2xl / divide-slate-100/60 / hover indigo）-->
          <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden divide-y divide-slate-100/60">
            <div
              v-for="row in group.rows"
              :key="row.key"
              class="flex items-start gap-4 px-5 py-4 transition-colors duration-200"
              :class="rowIsLocked(row) ? 'bg-slate-50/80 opacity-60' : 'hover:bg-indigo-50/60'"
            >
              <!-- Resource info（左主，可伸展；三行緊湊，字體整體放大一級）-->
              <div class="flex-1 min-w-0 space-y-2">
                <!-- Line 1：name + kind badge + code + route path（讓 user 看出對應的前端 page）-->
                <div class="flex items-center gap-2 flex-wrap leading-none">
                  <span class="text-base font-semibold text-slate-800">{{ row.displayName }}</span>
                  <span
                    class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-semibold border shrink-0 leading-none"
                    :class="kindMeta(row).chipClass"
                  >
                    <v-icon size="12" :color="kindIconColor(kindOf(row))">{{ kindMeta(row).icon }}</v-icon>
                    {{ $t(kindMeta(row).labelKey) }}
                  </span>
                  <span class="text-xs font-mono text-slate-400">{{ row.key }}</span>
                  <span
                    class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-semibold border shrink-0 leading-none"
                    :class="classificationMeta(row).chipClass"
                    :title="$t(classificationMeta(row).labelKey)"
                  >
                    <v-icon size="11" :color="classificationMeta(row).iconColor">{{ classificationMeta(row).icon }}</v-icon>
                    {{ $t(classificationMeta(row).labelKey) }}
                  </span>
                  <span
                    v-if="routePathOf(row.key)"
                    class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-mono text-sky-700 bg-sky-50 border border-sky-200 leading-none"
                    :title="$t('permission_resource_route_hint')"
                  >
                    <v-icon size="10" color="#0369a1">mdi-routes</v-icon>
                    {{ routePathOf(row.key) }}
                  </span>
                </div>
                <!-- Line 2：description -->
                <div v-if="row.description" class="text-sm text-slate-600 leading-snug">
                  {{ row.description }}
                </div>
                <!-- Line 3：action chips（左）+ last changed（右 ml-auto，inline 不再佔獨立欄）-->
                <div class="flex items-center gap-1.5 flex-wrap leading-none">
                  <span
                    v-for="a in supportedActionsList(row)"
                    :key="a"
                    class="text-xs font-medium px-2 py-1 rounded border leading-none"
                    :class="actionChipColor(a)"
                  >
                    {{ $t('permission_action_' + a) }}
                  </span>
                  <div class="ml-auto text-xs text-slate-400 tabular-nums leading-none">
                    <template v-if="row.legacyPermissionChangedAt">
                      {{ formatTime(row.legacyPermissionChangedAt) }} · by {{ row.legacyPermissionChangedBy || '—' }}
                    </template>
                    <span v-else class="italic">{{ $t('permission_never_switched') }}</span>
                  </div>
                </div>
              </div>

              <!-- Right：status badge + toggle（feature 子模組不獨立切換，跟 parent page 走，故 toggle 隱藏）-->
              <div class="shrink-0 flex items-center gap-3 pt-1">
                <span
                  class="inline-flex items-center gap-1.5 text-sm font-semibold leading-none whitespace-nowrap"
                  :class="row.useLegacyPermission ? 'text-amber-700' : 'text-emerald-700'"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full shrink-0"
                    :class="row.useLegacyPermission ? 'bg-amber-500' : 'bg-emerald-500'"
                    aria-hidden="true"
                  />
                  {{ row.useLegacyPermission ? $t('permission_legacy_role') : $t('permission_new_permission') }}
                </span>
                <template v-if="kindOf(row) === 'feature'">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-500 whitespace-nowrap"
                    :title="$t('permission_inherit_from_parent_hint')"
                  >
                    <v-icon size="11" color="#64748b" aria-hidden="true">mdi-link-variant</v-icon>
                    {{ $t('permission_inherit_from_parent') }}
                  </span>
                </template>
                <button
                  v-else
                  type="button"
                  role="switch"
                  :aria-checked="!row.useLegacyPermission"
                  :aria-label="`${row.displayName} - ${$t('permission_toggle_aria_label')}`"
                  class="relative shrink-0 w-11 h-6 rounded-full overflow-hidden transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                  :class="[
                    rowIsLocked(row) ? 'cursor-not-allowed bg-slate-200' : 'cursor-pointer',
                    !rowIsLocked(row) && row.useLegacyPermission ? 'bg-slate-300 focus-visible:ring-slate-400' : '',
                    !rowIsLocked(row) && !row.useLegacyPermission ? 'bg-emerald-500 focus-visible:ring-emerald-400' : ''
                  ]"
                  :disabled="togglingKey === row.key || rowIsLocked(row)"
                  @click="!rowIsLocked(row) && confirmToggle(row)"
                >
                  <!-- slider：用 left 絕對定位（避免 translate 在不同縮放或 container 下跑出邊界）-->
                  <span
                    class="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-200"
                    :class="row.useLegacyPermission ? 'left-0.5' : 'left-[22px]'"
                    aria-hidden="true"
                  ></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="flex flex-col items-center py-16 text-slate-400">
        <v-icon size="48" color="#cbd5e1" aria-hidden="true">mdi-magnify-close</v-icon>
        <div class="mt-3 text-sm">{{ $t('permission_no_match') }}</div>
      </div>
    </div>

    <!-- Confirm dialog — 統一用 v-dialog（取代自製 overlay，跟 Users/Roles 語彙一致） -->
    <v-dialog v-model="confirmDialog" max-width="440" persistent>
      <v-card v-if="pending" class="rounded-xl">
        <v-card-title class="text-base font-bold">
          {{ $t('permission_confirm_switch_title') }}
        </v-card-title>
        <v-card-text class="text-sm text-slate-600">
          <p class="mb-3">{{ $t('permission_confirm_switch_desc') }}</p>
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
              <v-icon size="20" color="#7c3aed" aria-hidden="true">mdi-shield-check-outline</v-icon>
            </div>
            <div class="min-w-0">
              <div class="font-semibold text-slate-800 truncate">{{ pending.displayName }}</div>
              <code class="text-xs text-slate-400">{{ pending.key }}</code>
            </div>
          </div>
          <div
            class="flex items-start gap-2 px-3 py-2.5 rounded-xl text-sm"
            :class="pending.targetFlag
              ? 'bg-amber-50 border border-amber-200 text-amber-800'
              : 'bg-emerald-50 border border-emerald-200 text-emerald-800'"
          >
            <v-icon
              size="16"
              :color="pending.targetFlag ? '#d97706' : '#059669'"
              class="shrink-0 mt-0.5"
              aria-hidden="true"
            >
              mdi-information-outline
            </v-icon>
            <span>
              {{ pending.targetFlag ? $t('permission_to_legacy_warn') : $t('permission_to_new_warn') }}
            </span>
          </div>
        </v-card-text>
        <v-card-actions class="px-5 pb-4">
          <v-spacer />
          <v-btn text @click="cancelToggle">{{ $t('btn_cancel') }}</v-btn>
          <v-btn color="primary" @click="doToggle">{{ $t('permission_confirm_switch') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="2500" role="alert" aria-live="polite">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
  </div>
</template>

<script>
import { HttpResource } from '@/js/http-Manager'
import PermissionPageHeader from '@/components/Permission/PermissionPageHeader'
import PermissionSubTabs from '@/components/Permission/PermissionSubTabs'
import { kindMeta, kindOf, kindIconColor, supportedActionsList, classifyResource, isReverseOrphan, isLocked } from '@/js/permission-helpers'
import { buildResourceRouteMap, buildFrontendDeclaredKeys } from '@/js/permission-catalog'
import router from '@/router'

const RESOURCE_ROUTE_MAP = buildResourceRouteMap(router.options.routes)
const FRONTEND_DECLARED_KEYS = buildFrontendDeclaredKeys(router.options.routes)

// 每個 action 用語意色分層（避免 6 顆 chip 同灰難辨）：
// view=slate 中性；create=emerald 正向；update=sky 資訊；delete=rose 危險；export=violet；approve=amber
const ACTION_CHIP_COLOR = {
  view: 'bg-slate-50 text-slate-600 border-slate-200',
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  update: 'bg-sky-50 text-sky-700 border-sky-200',
  delete: 'bg-rose-50 text-rose-700 border-rose-200',
  export: 'bg-violet-50 text-violet-700 border-violet-200',
  approve: 'bg-amber-50 text-amber-700 border-amber-200'
}

const CLASS_BADGE = {
  healthy: {
    chipClass: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    icon: 'mdi-check-circle-outline',
    iconColor: '#059669',
    labelKey: 'permission_class_healthy'
  },
  'legacy-pending': {
    chipClass: 'bg-slate-100 text-slate-600 border-slate-300',
    icon: 'mdi-clock-outline',
    iconColor: '#64748b',
    labelKey: 'permission_class_legacy_pending'
  },
  'half-impl': {
    chipClass: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: 'mdi-alert-circle-outline',
    iconColor: '#d97706',
    labelKey: 'permission_class_half_impl'
  },
  'true-orphan': {
    chipClass: 'bg-rose-100 text-rose-700 border-rose-200',
    icon: 'mdi-skull-outline',
    iconColor: '#e11d48',
    labelKey: 'permission_class_true_orphan'
  }
}

export default {
  name: 'ResourceSwitcher',
  components: { PermissionPageHeader, PermissionSubTabs },
  data () {
    return {
      items: [],
      loading: false,
      keyword: '',
      pending: null, // { key, displayName, targetFlag, originalFlag }
      togglingKey: null,
      snackbar: { show: false, text: '', color: 'success' }
    }
  },
  computed: {
    dbKeySet () {
      return new Set(this.items.map(r => r.key))
    },
    filteredItems () {
      const kw = this.keyword.trim().toLowerCase()
      if (!kw) return this.items
      return this.items.filter(r =>
        [r.displayName, r.key, r.category]
          .filter(Boolean)
          .some(s => String(s).toLowerCase().includes(kw))
      )
    },
    filteredGroups () {
      const map = {}
      for (const r of this.filteredItems) {
        const cat = r.category || ''
        if (!map[cat]) map[cat] = []
        map[cat].push(r)
      }
      return Object.keys(map).sort().map(cat => ({
        category: cat,
        rows: map[cat]
      }))
    },
    headerStats () {
      const total = this.items.length
      const legacy = this.items.filter(r => r.useLegacyPermission).length
      return [
        { label: this.$t('permission_total_label'), value: total, icon: 'mdi-folder-outline' },
        { label: this.$t('permission_legacy_count_label'), value: legacy, icon: 'mdi-shield-alert-outline', iconColor: '#d97706' },
        { label: this.$t('permission_new_count_label'), value: total - legacy, icon: 'mdi-shield-check-outline', iconColor: '#059669' }
      ]
    },
    enforceCoverage () {
      // 分母僅含可治理範圍（catalog 內）：true-orphan（DB 殘留）被鎖死、永遠無法 enforce，計入會讓覆蓋率結構性達不到 100%
      const governable = this.items.filter(r => r.isInCatalog)
      if (!governable.length) return 0
      const enforced = governable.filter(r => !r.useLegacyPermission).length
      return Math.round((enforced / governable.length) * 100)
    },
    healthRate () {
      const governable = this.items.filter(r => r.isInCatalog)
      if (!governable.length) return 0
      const healthy = governable.filter(r =>
        classifyResource(r, this.dbKeySet, FRONTEND_DECLARED_KEYS) === 'healthy'
      ).length
      return Math.round((healthy / governable.length) * 100)
    },
    reverseOrphanKeys () {
      return [...FRONTEND_DECLARED_KEYS]
        .filter(key => isReverseOrphan(key, this.dbKeySet, FRONTEND_DECLARED_KEYS))
        .sort()
    },
    // v-dialog bridge：pending != null 顯示；使用者按背景遮罩或 Esc 關閉時自動清 pending
    confirmDialog: {
      get () { return this.pending !== null },
      set (v) { if (!v) this.pending = null }
    }
  },
  async mounted () {
    await this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        const res = await HttpResource.GetAll()
        if (res && res.status === 200) this.items = res.data || []
      } finally {
        this.loading = false
      }
    },
    formatTime (v) {
      if (!v) return ''
      return this.$moment(v).format('YYYY-MM-DD HH:mm')
    },
    supportedActionsList,
    actionChipColor (a) {
      return ACTION_CHIP_COLOR[a] || 'bg-slate-100 text-slate-600'
    },
    routePathOf (key) {
      return RESOURCE_ROUTE_MAP[key] || ''
    },
    kindMeta,
    kindOf,
    kindIconColor,
    classificationMeta (row) {
      const cls = classifyResource(row, this.dbKeySet, FRONTEND_DECLARED_KEYS)
      return CLASS_BADGE[cls] || CLASS_BADGE.healthy
    },
    rowIsLocked (row) {
      return isLocked(row, this.dbKeySet, FRONTEND_DECLARED_KEYS)
    },
    confirmToggle (row) {
      this.pending = {
        key: row.key,
        displayName: row.displayName,
        targetFlag: !row.useLegacyPermission,
        originalFlag: row.useLegacyPermission
      }
    },
    cancelToggle () {
      this.pending = null
    },
    async doToggle () {
      if (!this.pending) return
      const { key, targetFlag } = this.pending
      this.togglingKey = key
      this.pending = null
      try {
        const res = await HttpResource.ToggleUseLegacy(key, targetFlag)
        if (res && res.status === 200) {
          this.snackbar = { show: true, color: 'success', text: this.$t('save_success') }
          await this.load()
          // 同步更新 permission store 的 isLegacy 快取，避免 sidebar / router guard 拿到 stale cache
          // 直接改 effectiveMap 對應 entry 不發 API，避免多切幾個 resource 就打爆後端。
          // 後端 page 切軌會 cascade 到 feature 子模組，快取同步要涵蓋，否則 $can(feature) 拿 stale 值直到 reload
          this.$store.commit('permission/setResourceLegacyFlag', { resourceKey: key, isLegacy: targetFlag })
          this.items
            .filter(r => r.parentKey === key && r.kind === 'feature')
            .forEach(r => {
              this.$store.commit('permission/setResourceLegacyFlag', { resourceKey: r.key, isLegacy: targetFlag })
            })
        } else {
          const msg = res && res.data && res.data.message
          this.snackbar = { show: true, color: 'error', text: msg || this.$t('save_fail') }
        }
      } finally {
        this.togglingKey = null
      }
    }
  }
}
</script>

<style scoped>
.permission-resources-view {
  margin: -20px;
  min-height: calc(100vh - 64px);
  padding-bottom: 24px;
}
</style>
